import React, { useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import {
  Check as CheckIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from '@mui/icons-material';
import { SV } from '../../automation/stratovaiTestIds';
import { getProjectId, getProjectName, isSameProject, sortProjectsByName } from './projectUtils';

const SidebarProjectSwitcher = ({
  selectedProject,
  projects = [],
  projectsLoading = false,
  projectsError = null,
  onProjectSelect,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [switching, setSwitching] = useState(false);
  const menuOpen = Boolean(anchorEl);
  const hasMultipleProjects = projects.length > 1;

  const sortedProjects = useMemo(() => sortProjectsByName(projects), [projects]);

  if (!selectedProject) {
    return null;
  }

  const projectName = getProjectName(selectedProject);
  const projectId = getProjectId(selectedProject);

  const handleOpenMenu = (event) => {
    if (!hasMultipleProjects || switching) return;
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleSelectProject = async (project) => {
    if (isSameProject(project, selectedProject)) {
      handleCloseMenu();
      return;
    }
    if (!onProjectSelect) return;

    setSwitching(true);
    try {
      await onProjectSelect(project);
    } finally {
      setSwitching(false);
      handleCloseMenu();
    }
  };

  return (
    <Box sx={{ mt: 1 }} data-testid={SV.nav.projectSwitcher}>
      {hasMultipleProjects ? (
        <Box
          component="button"
          type="button"
          data-testid={SV.nav.projectSwitcherTrigger}
          onClick={handleOpenMenu}
          disabled={switching}
          aria-haspopup="listbox"
          aria-expanded={menuOpen ? 'true' : 'false'}
          aria-label={`Switch project. Current project ${projectName}`}
          sx={{
            display: 'block',
            width: '100%',
            p: 0,
            m: 0,
            border: 0,
            background: 'none',
            textAlign: 'left',
            cursor: switching ? 'wait' : 'pointer',
            color: 'inherit',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.25, flex: 1 }}>
              {projectName}
            </Typography>
            {switching ? (
              <CircularProgress size={14} />
            ) : (
              <KeyboardArrowDownIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            )}
          </Box>
          <Typography variant="caption" color="text.secondary">
            Project ID: {projectId}
          </Typography>
        </Box>
      ) : (
        <Box data-testid={SV.nav.projectSwitcherStatic}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.25 }}>
            {projectName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Project ID: {projectId}
          </Typography>
        </Box>
      )}

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleCloseMenu}
        data-testid={SV.nav.projectSwitcherMenu}
        PaperProps={{
          sx: { minWidth: 240, maxWidth: 320 },
        }}
      >
        <MenuItem disabled sx={{ opacity: '1 !important' }}>
          <ListItemText
            primary="Switch Project"
            primaryTypographyProps={{ variant: 'caption', color: 'text.secondary', fontWeight: 600 }}
          />
        </MenuItem>
        {projectsLoading ? (
          <MenuItem disabled>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <CircularProgress size={16} />
            </ListItemIcon>
            <ListItemText primary="Loading projects..." />
          </MenuItem>
        ) : null}
        {!projectsLoading && projectsError ? (
          <MenuItem disabled>
            <ListItemText primary={projectsError} secondary="Try again later" />
          </MenuItem>
        ) : null}
        {!projectsLoading && !projectsError
          ? sortedProjects.map((project) => {
              const itemId = getProjectId(project);
              const selected = isSameProject(project, selectedProject);
              return (
                <MenuItem
                  key={String(itemId)}
                  data-testid={SV.nav.projectSwitcherOption(itemId)}
                  selected={selected}
                  onClick={() => handleSelectProject(project)}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {selected ? <CheckIcon fontSize="small" /> : null}
                  </ListItemIcon>
                  <ListItemText primary={getProjectName(project)} />
                </MenuItem>
              );
            })
          : null}
      </Menu>
    </Box>
  );
};

export default SidebarProjectSwitcher;
